import { Box, Chip, makeStyles } from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';

import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { CustomPalette } from '@/config/types';
import { useCustomTheme } from '@/hooks/useCustomTheme';

const VEHICLE_CONDITION_OPTIONS = [
  { isNewVehicle: true, tId: 'NEW_VEHICLE_CONDITION' },
  { isNewVehicle: false, tId: 'USED_VEHICLE_CONDITION' },
];

interface VehicleConditionChipsStylesProps {
  palette: CustomPalette;
  borderRadius: number;
}

const useStyles = makeStyles((theme) => ({
  chip: ({ palette, borderRadius }: VehicleConditionChipsStylesProps) => ({
    minWidth: 72,
    borderRadius,
    border: `1px solid ${palette.grey[300]}`,
    backgroundColor: palette.common.white,
    color: palette.text.primary,
    fontWeight: theme.typography.fontWeightBold as number,
    '&:hover, &:focus': {
      backgroundColor: palette.common.white,
      borderColor: palette.text.title,
    },
  }),
  selected: ({ palette }: VehicleConditionChipsStylesProps) => ({
    backgroundColor: palette.text.title,
    borderColor: palette.text.title,
    color: palette.common.white,
    '&:hover, &:focus': {
      backgroundColor: palette.text.title,
    },
  }),
}));

export const VehicleConditionChips = ({
  disabled,
  selectedTextId,
}: {
  disabled?: boolean;
  selectedTextId?: string;
}) => {
  const { palette, customBorderRadius } = useCustomTheme();
  const classes = useStyles({ palette, borderRadius: customBorderRadius.chip });
  const { control } = useFormContext();

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <Font variant="subtitle1" color="title">
        <strong>
          <Text tId="VEHICLE_CONDITION" />
        </strong>
      </Font>
      {disabled && selectedTextId ? (
        <Box display="flex" gridGap={8} role="group">
          <Chip key={'selected'} size="small" label={<Text tId={selectedTextId} />} />
        </Box>
      ) : (
        <Controller
          disabled={disabled}
          name="item.isNewVehicle"
          control={control}
          render={({ field }) => (
            <Box display="flex" gridGap={8} role="group">
              {VEHICLE_CONDITION_OPTIONS.map(({ isNewVehicle, tId }) => {
                const selected = !!field.value === isNewVehicle;

                return (
                  <Chip
                    key={tId}
                    size="small"
                    label={<Text tId={tId} />}
                    aria-pressed={selected}
                    clickable
                    onClick={() => field.onChange(isNewVehicle)}
                    className={selected ? `${classes.chip} ${classes.selected}` : classes.chip}
                  />
                );
              })}
            </Box>
          )}
        />
      )}
    </Box>
  );
};
